import Button from "devextreme-react/button";
import { useContext } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { showErrorMessage, showSuccessMessage } from "../../../../utils/messages";
import { useUpdatePurchaseOrder } from "../api/update-purchase-order";
import { PurchaseOrderContext } from "../context/PurchaseOrderContext";
import { PurchaseOrderProgressStep } from "../types/PurchaseOrderProgressStep";
import { PurchaseOrderUpdateType } from "../types/UpdatePurchaseOrderParams";
import { LoadPagePanel } from "../../../../components";
import { CompanyTypes, OrderStatus } from "../../../../types/enums";
import { useAuth } from "../../../../contexts/auth";

export default function SaveButton(params: { step: PurchaseOrderProgressStep }) {

	const navigate = useNavigate();
	const location = useLocation();

	const [searchParams] = useSearchParams();
	const returnUrl = encodeURIComponent(searchParams.get("returnUrl") ?? "");

	const { order, refetchOrder } = useContext(PurchaseOrderContext);

	const { user } = useAuth();
	const urlFolder = user?.companyTypeId === CompanyTypes.MFR ? "manufacturer" :
		user?.companyTypeId === CompanyTypes.SERVICE_PROVIDER ? "service-provider" : "distributor";

	let updateType: PurchaseOrderUpdateType;
	let validate: () => boolean

	switch (params.step) {
		case PurchaseOrderProgressStep.Validate:
			updateType = PurchaseOrderUpdateType.Validate;
			validate = () => { return true; }
			break;

		case PurchaseOrderProgressStep.EnterServiceDate:
			updateType = PurchaseOrderUpdateType.Service;
			validate = () => { return true; }
			break;

		case PurchaseOrderProgressStep.ValidateDelivery:
			updateType = PurchaseOrderUpdateType.ValidateDelivery;
			validate = () => { return true; }
			break;

		case PurchaseOrderProgressStep.Ship:
			updateType = PurchaseOrderUpdateType.Ship;
			validate = () => {

				let valid = true;
				for (const product of order.details.products) {
					if (!product.isGraniteSlab && (product.qtyShipped ?? 0) < product.qtyOrdered) {
						showErrorMessage('You can not ship a partial order');
						valid = false;
						break;
					}
				}

				for (const product of order.details.products) {
					if (product.isGraniteSlab && (product.qtyShipped ?? 0) < 25 && !product.backOrdered) {
						showErrorMessage('Qty Shipped can not be less than 25');
						valid = false;
						break;
					}
				}

				var hasShippedDate = !!order.details.products.find(x => !!x.shippedDate);
				var hasBackOrdered = !!order.details.products.find(x => x.backOrdered)

				if (!hasShippedDate && !hasBackOrdered) {
					showErrorMessage('Missing Required Field: Actual Ship Date');
					valid = false;
				}

				return valid;
			}
			break;
		case PurchaseOrderProgressStep.DeliveryDetails:
			updateType = PurchaseOrderUpdateType.SubmitDelivery;
			validate = () => {

				let valid = true;
				for (const product of order.details.products) {
					if (!product.isGraniteSlab && (product.qtyDelivered ?? 0) < product.qtyOrdered) {
						showErrorMessage('You can not delivery a partial order');
						valid = false;
						break;
					}
				}

				for (const product of order.details.products) {
					if (product.isGraniteSlab && (product.qtyDelivered ?? 0) < 25 && !product.backOrdered) {
						showErrorMessage('Qty Delivered can not be less than 25');
						valid = false;
						break;
					}
				}

				var hasDeliveredDate = !!order.details.products.find(x => !!x.deliveredDate);
				var hasBackOrdered = !!order.details.products.find(x => x.backOrdered)

				if (!hasDeliveredDate && !hasBackOrdered) {
					showErrorMessage('Missing Required Field: Actual Delivery Date');
					valid = false;
				}

				return valid;
			}
			break;
	}

	const updatePurchaseOrder = useUpdatePurchaseOrder(
		{
			purchaseOrderID: order.details.purchaseOrderID,
			validatedShipDate: order.details.validatedShipDate,
			validatedDeliveryDate: order.details.validatedDeliveryDate,
			serviceInitiatedDate: order.details.serviceInitiatedDate,
			GS1128: order.details.GS1128,
			products: order.details.products.map(x => {
				return {
					poProductID: x.poProductID,
					qtyShipped: x.qtyShipped,
					qtyDelivered: x.qtyDelivered,
					carrier: x.carrier,
					trackingNbr: x.trackingNbr,
					shippedDate: x.shippedDate,
					deliveredDate: x.deliveredDate,
					backOrdered: x.backOrdered,
					estimatedShipDate: x.estimatedShipDate,
					estimatedDeliveryDate: x.estimatedDeliveryDate
				};
			}),
			updateType: updateType!
		},
		(result) => {
			showSuccessMessage('Purchase order has been updated');
			refetchOrder()
				.then(() => {
					if (params.step !== PurchaseOrderProgressStep.Info) {
						const newActionRequiredID = result.activeARs[0];

						const to = `/${urlFolder}/purchase-order/${order.details.purchaseOrderID}/${newActionRequiredID}` +
							(!!returnUrl ? '?returnUrl=' + returnUrl : '');

						if ((newActionRequiredID === 350 || newActionRequiredID === 351 ||
							newActionRequiredID === 360 || newActionRequiredID === 380) &&
							location.pathname !== to) {
							navigate(to);
						}
					}
				})
		},
		(error) => showErrorMessage(error)
	);

	return (
		<>
			<Button
				text="Save Changes"
				icon="Inscyth-Save"
				visible={order.details.orderStatusID === OrderStatus.Open}
				disabled={!order.modified || updatePurchaseOrder.isLoading}
				onClick={(e) => {
					let result = e.validationGroup.validate();
					if (result.isValid) {
						if (validate()) {
							updatePurchaseOrder.mutate();
						}
					}
				}} >
			</Button>
			<LoadPagePanel visible={updatePurchaseOrder.isLoading} />
		</>
	)
}
